import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import QRCode from "react-qr-code";
import { useLocation } from "react-router-dom";
import useNotification from "../../hooks/useNotification";
import service from "../../services/check";
import InvalidToken from "../InvalidToken";
import LoadingPage from "../LoadingPage";
import OrderNotFound from "../OrderNotFound";
import ServerError from "../ServerError";
import "./Voucher.css"; // External CSS file for styling


const VoucherPage = () => {
  const location = useLocation();
  const [data, setData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorType, setErrorType] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const token = new URLSearchParams(location.search).get("token");

  // Update timestamp every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (token) {
      setErrorType(null);
      setIsLoading(true);
      service
        .validateToken({ token })
        .then((response) => {
          setData(response.data);
        })
        .catch((error) => {
          console.error("Error validating token:", error);

          // Check for specific error status codes
          if (error?.response?.status === 404) {
            setErrorType("order_not_found");
          } else if (error?.response?.status === 401) {
            setErrorType("invalid_token");
          } else {
            setErrorType("server_error");
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, [token]);

  if (isLoading) {
    return <LoadingPage />;
  }

  // Handle different error types
  if (errorType === "order_not_found") {
    return <OrderNotFound />;
  }

  if (errorType === "invalid_token") {
    return <InvalidToken />;
  }

  if (errorType === "server_error") {
    return <ServerError />;
  }

  return (
    <div className="voucher-page">
      {/* Sticky Timestamp */}
      <Helmet>
        <title>Voucher Page</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div
        style={{
          position: "sticky",
          top: 0,
          backgroundColor: "#f5f5f5",
          padding: "8px 16px",
          textAlign: "center",
          fontSize: "12px",
          color: "#666",
          borderBottom: "1px solid #ddd",
          zIndex: 1000,
        }}
      >
        Generated: {currentTime.toLocaleString()}
      </div>

      {data &&
        data?.tickets?.map((ticket: any, index: any) => (
          <div key={index} className="voucher-container">
            <div className="header">
              <div className="header-logo">
                <img
                  src={process.env.REACT_APP_BACKEND_URL + data.photo}
                  alt="Logo"
                  className="logo"
                />
              </div>
              <div className="header-title">
                <h1>{ticket.event_name}</h1>
                <p>{data.location}</p>
              </div>
              <div className="header-info">
                <h2>E-Voucher</h2>
                <p>Valid for 1 Person</p>
                <p>(Not for Resale)</p>
              </div>
            </div>

            {/* Ticket Holder Information Section */}
            <div className="order-information">
              <h2>Ticket Holder Information</h2>
              <div className="order-details">
                <div className="order-left">
                  <div className="order-row">
                    <span className="order-label">Buyer Name</span>
                    <span>:</span>
                    <span className="order-value"> {ticket.buyer_name}</span>
                  </div>
                  <div className="order-row">
                    <span className="order-label">Name</span>
                    <span>:</span>
                    <span className="order-value"> {ticket.name}</span>
                  </div>
                  <div className="order-row">
                    <span className="order-label">Category</span>
                    <span>:</span>
                    <span className="order-value"> {ticket.kategori}</span>
                  </div>
                  {ticket.uses_seating && (
                    <div className="order-row">
                      <span className="order-label">Seat Number</span>
                      <span>:</span>
                      <span className="order-value">
                        {ticket.is_seated ? (
                          <em>{ticket.seat || 'Not assigned'}</em>
                        ) : (
                          <em>NO SEATING CATEGORY</em>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="order-row">
                    <span className="order-label">Email</span>
                    <span>:</span>
                    <span className="order-value"> {ticket.email}</span>
                  </div>
                  <div className="order-row">
                    <span className="order-label">Phone Number</span>
                    <span>:</span>
                    <span className="order-value">{ticket.phone}</span>
                  </div>
                  {ticket.gender && (
                    <div className="order-row">
                      <span className="order-label">Gender</span>
                      <span>:</span>
                      <span className="order-value"> {ticket.gender}</span>
                    </div>
                  )}
                  {ticket.identity_number && (
                    <div className="order-row">
                      <span className="order-label">ID Number</span>
                      <span>:</span>
                      <span className="order-value"> {ticket.identity_number}</span>
                    </div>
                  )}
                  {ticket.date_of_birth && (
                    <div className="order-row">
                      <span className="order-label">Date of Birth</span>
                      <span>:</span>
                      <span className="order-value"> {new Date(ticket.date_of_birth).toLocaleDateString('en-GB')}</span>
                    </div>
                  )}
                  {ticket.company && (
                    <div className="order-row">
                      <span className="order-label">Company</span>
                      <span>:</span>
                      <span className="order-value"> {ticket.company}</span>
                    </div>
                  )}
                  <div className="order-row">
                    <span className="order-label">Venue</span>
                    <span>:</span>
                    <span className="order-value">{data.location}</span>
                  </div>
                  <div className="order-row">
                    <span className="order-label">Valid Date</span>
                    <span>:</span>
                    <span className="order-value">{ticket.schedule}</span>
                  </div>
                  {ticket.address && (
                    <div className="order-row">
                      <span className="order-label">Address</span>
                      <span>:</span>
                      <span className="order-value"> {ticket.address}</span>
                    </div>
                  )}
                  {ticket.emergency_contact && (
                    <div className="order-row">
                      <span className="order-label">Emergency Contact</span>
                      <span>:</span>
                      <span className="order-value"> {ticket.emergency_contact}</span>
                    </div>
                  )}
                  {ticket.dietary_requirements && (
                    <div className="order-row">
                      <span className="order-label">Dietary Requirements</span>
                      <span>:</span>
                      <span className="order-value"> {ticket.dietary_requirements}</span>
                    </div>
                  )}
                  {ticket.special_needs && (
                    <div className="order-row">
                      <span className="order-label">Special Needs</span>
                      <span>:</span>
                      <span className="order-value"> {ticket.special_needs}</span>
                    </div>
                  )}
                </div>

                {/* QR Code Section */}
                <div className="qr-code-section">
                  <QRCode value={ticket.code} size={128} />
                  <div className="ticket-id">
                    <p>{ticket.code}</p>
                  </div>
                  <div style={{ textAlign: "center", fontSize: "10px" }}>
                    <p>
                      TICKET {index + 1} OF {data.tickets.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms and Conditions Section */}
            <div className="terms">
              <h2>Terms & Conditions</h2>
              <ol>
                <li>One (1) ticket is valid for one (1) person.</li>
                <li>
                  Purchased tickets cannot be returned or refunded for any reason.
                </li>
                <li>Purchased tickets cannot have their schedule changed.</li>
                <li>Buyers must provide personal data when ordering.</li>
                <li>
                  Ticket sales may be stopped/started at any time according to the promoter's policy.
                </li>
                <li>
                  This ticket cannot be transferred or resold.
                </li>
                <li>
                  The Committee/Organizer/Promoter is not responsible for purchasing TICKETS through scalpers/places/channels/platforms that are not official sales partners of {data.tickets?.[0]?.event_name || 'Event'} TICKETS.
                </li>
                <li>
                  Visitors are fully responsible for the security of themselves and all their personal belongings.
                </li>
                <li>
                  Visitors are prohibited from recording with professional cameras for commercial purposes.
                </li>
                <li>Visitors are prohibited from taking photos using flash.</li>
                <li>
                  Visitors are prohibited from carrying firearms, sharp weapons, and dangerous objects.
                </li>
                <li>
                  Visitors are prohibited from bringing food and drinks from outside.
                </li>
                <li>
                  This category of ticket cannot be transferred for any reason.
                </li>
                <li>
                  The Organizer has the right to:
                  <ul>
                    <li>
                      Refuse entry to visitors if the TICKET has been used by someone else/is not valid.
                    </li>
                    <li>
                      Process or file lawsuits, both civil or criminal, against visitors who obtain TICKETS illegally.
                    </li>
                    <li>
                      In Force Majeure conditions such as natural disasters, war, epidemics, etc., the committee has the right to cancel the event and/or move the time and place of the event without prior notice.
                    </li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        ))}
    </div>
  );
};

export default VoucherPage;
